import {
  BenefitDocumentDto,
  BenefitDocumentType,
  CreateAutomaticApplicationDto,
  SpecialEnrollmentReasonType,
} from '@zorro/clients';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { UseFormReturn } from 'react-hook-form';

import {
  isPersistedDocument,
  usePersistUploadedDocuments,
} from '../../BenefitDocuments';
import { CarrierApplicationSubmitFormFields } from './CarrierCompleteStepUtils';

/**
 * What the documentation section starts with: the enrollment's QLE documents are its existing
 * proof, and the ICHRA letter takes their place when the offer itself is the reason.
 */
export const useSpecialEnrollmentDefaults = (
  employeeId: string,
  benefitId: string | undefined,
  documents: BenefitDocumentDto[]
) => {
  const { data: availableSubmissionMethods } = useMonolithQuery({
    method: 'applicationsControllerGetAvailableSubmissionMethods',
    params: benefitId && [employeeId, benefitId],
  });

  return {
    specialEnrollmentDocuments: documents.filter(
      ({ type }) => type === BenefitDocumentType.QLE
    ),
    withIchraLetterAsSepProof:
      availableSubmissionMethods?.specialEnrollment?.type ===
      SpecialEnrollmentReasonType.OFFERED_ICHRA,
  };
};

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
};

/**
 * Saves the files the agent added as QLE documents on the enrollment — reusable next time, and
 * referenceable by id — and returns what the submission should send as its special enrollment
 * proof. Null when a file couldn't be saved: submitting then would reach the carrier with an
 * incomplete set.
 */
export const useSpecialEnrollmentProof = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  submissionForm,
}: Props) => {
  const persistDocuments = usePersistUploadedDocuments({
    employerId,
    employeeId,
    onboardingPeriodId,
    documentType: BenefitDocumentType.QLE,
    isSharedWithEmployee: true,
    documentLabel: 'special enrollment documentation',
  });

  return async (): Promise<CreateAutomaticApplicationDto | null> => {
    const documents = submissionForm.getValues('specialEnrollmentDocuments');
    const result = await persistDocuments(
      Array.isArray(documents) ? documents : []
    );
    submissionForm.setValue('specialEnrollmentDocuments', result.documents, {
      shouldDirty: true,
    });
    if (!result.isSuccess) return null;

    return {
      sepDocumentIds: result.documents
        .filter(isPersistedDocument)
        .map(({ id }) => id),
      withIchraLetter: Boolean(
        submissionForm.getValues('withIchraLetterAsSepProof')
      ),
    };
  };
};
